import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import {
  AlertCircle,
  BadgeCheck,
  Copy,
  ExternalLink,
  Instagram,
  Loader2,
  Music2,
  RefreshCw,
  Send,
  Share2,
  Youtube,
  Zap,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

type RewardPlatform = "youtube" | "x" | "instagram" | "tiktok";

type RewardsBannerProps = {
  projectId: string | null;
  exportedVideoUrl?: string | null;
  readOnly?: boolean;
  // Controlled mode (e.g. the home-page Rewards menu): the parent owns the
  // open state and no floating trigger button is rendered. In this mode the
  // dialog works without a project — account connect + schedule + tracked
  // posts are all account-scoped; only the submit action needs a project.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

const PLATFORMS: Array<{
  id: RewardPlatform;
  label: string;
  handlePlaceholder: string;
}> = [
  {
    id: "youtube",
    label: "YouTube",
    handlePlaceholder: "@yourchannel or channel URL",
  },
  { id: "x", label: "X", handlePlaceholder: "yourhandle or profile URL" },
  {
    id: "instagram",
    label: "Instagram",
    handlePlaceholder: "yourhandle or profile URL",
  },
  {
    id: "tiktok",
    label: "TikTok",
    handlePlaceholder: "yourhandle or profile URL",
  },
];

function platformIcon(platform: RewardPlatform) {
  if (platform === "youtube") return <Youtube className="h-4 w-4" />;
  if (platform === "instagram") return <Instagram className="h-4 w-4" />;
  if (platform === "tiktok") return <Music2 className="h-4 w-4" />;
  return <span className="text-sm font-black leading-none">X</span>;
}

function formatNumber(value: unknown) {
  const n = Number(value) || 0;
  return n.toLocaleString();
}

function formatDate(value: unknown) {
  if (!value) return "Not checked yet";
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return "Not checked yet";
  return date.toLocaleString();
}

function formatUsdValue(value: unknown) {
  const n = Number(value) || 0;
  return `~$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function statusTone(status: string) {
  if (status === "verified" || status === "tracking" || status === "completed") {
    return "border-emerald-400/30 bg-emerald-400/10 text-emerald-200";
  }
  if (status === "needs_review" || status === "manual_review") {
    return "border-amber-400/30 bg-amber-400/10 text-amber-200";
  }
  if (status === "failed" || status === "rejected") {
    return "border-red-400/30 bg-red-400/10 text-red-200";
  }
  return "border-white/10 bg-white/5 text-white/70";
}

function StatusPill({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${statusTone(status)}`}
    >
      {status.replace("_", " ")}
    </span>
  );
}

function verificationPlacement(platform: RewardPlatform) {
  if (platform === "youtube") return "YouTube channel description";
  if (platform === "x") return "X bio";
  if (platform === "tiktok") return "TikTok bio";
  return "Instagram bio";
}

function verificationProfileLink(platform: RewardPlatform) {
  if (platform === "youtube") {
    return {
      href: "https://studio.youtube.com/",
      label: "Open YouTube Studio",
    };
  }
  if (platform === "x") {
    return {
      href: "https://x.com/settings/profile",
      label: "Open X profile settings",
    };
  }
  if (platform === "tiktok") {
    return {
      href: "https://www.tiktok.com/",
      label: "Open TikTok (edit bio from your profile)",
    };
  }
  return {
    href: "https://www.instagram.com/accounts/edit/",
    label: "Open Instagram profile",
  };
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Embedded browsers can deny Clipboard API access. Fall through.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-9999px";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";

  const activeElement =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  } finally {
    document.body.removeChild(textarea);
    activeElement?.focus();
  }

  return copied;
}

function selectVisibleVerificationCode(code: string): boolean {
  const elements = Array.from(
    document.querySelectorAll<HTMLElement>("[data-reward-verification-code]"),
  );
  const element =
    elements.find((node) => node.textContent?.trim() === code) ?? elements[0];
  const selection = window.getSelection();
  if (!element || !selection) return false;

  const range = document.createRange();
  range.selectNodeContents(element);
  selection.removeAllRanges();
  selection.addRange(range);
  element.scrollIntoView({ block: "nearest", inline: "nearest" });
  return true;
}

export default function RewardsBanner({
  projectId,
  exportedVideoUrl,
  readOnly = false,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: RewardsBannerProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [selectedPlatform, setSelectedPlatform] =
    useState<RewardPlatform>("youtube");
  const [handle, setHandle] = useState("");
  const [postUrl, setPostUrl] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const utils = trpc.useUtils();

  const summaryQuery = trpc.rewards.summary.useQuery(undefined, {
    enabled: open,
    staleTime: 15_000,
  });
  const beginConnect = trpc.rewards.beginConnect.useMutation({
    onSuccess: async (account) => {
      setSelectedAccountId(account.id);
      await utils.rewards.summary.invalidate();
      toast.success("Verification code created.");
    },
    onError: (error) => toast.error(error.message),
  });
  const verifyAccount = trpc.rewards.verifyAccount.useMutation({
    onSuccess: async () => {
      await utils.rewards.summary.invalidate();
      toast.success("Social account verified.");
    },
    onError: (error) => toast.error(error.message),
  });
  const submitLink = trpc.rewards.submitLink.useMutation({
    onSuccess: async (result) => {
      await utils.rewards.summary.invalidate();
      setPostUrl("");
      toast.success(
        result.warning
          ? "Post submitted for rewards review."
          : "Post submitted. Tracking starts from its current views.",
      );
    },
    onError: (error) => toast.error(error.message),
  });
  const refreshSubmission = trpc.rewards.refreshSubmission.useMutation({
    onSuccess: async (result) => {
      await utils.rewards.summary.invalidate();
      toast.success(result.message || "Reward status refreshed.");
    },
    onError: (error) => toast.error(error.message),
  });

  const data = summaryQuery.data as any;
  const accounts = data?.accounts ?? [];
  const submissions = data?.submissions ?? [];
  const events = data?.events ?? [];
  const verifiedAccounts = accounts.filter(
    (account: any) => account.status === "verified",
  );
  const selectedAccount =
    accounts.find(
      (account: any) => account.platform === selectedPlatform,
    ) ?? null;
  const submitAccounts = verifiedAccounts.length ? verifiedAccounts : [];
  const activeSubmitAccountId =
    selectedAccountId ||
    submitAccounts[0]?.id ||
    "";
  const canSubmit = Boolean(projectId && exportedVideoUrl && activeSubmitAccountId);
  const selectedPlatformConfig = PLATFORMS.find(
    (platform) => platform.id === selectedPlatform,
  )!;
  const schedule = data?.schedules?.[selectedPlatform] ?? [];
  const availability = data?.providerAvailability?.[selectedPlatform];
  const accountVerificationAvailable =
    typeof availability === "boolean"
      ? availability
      : Boolean(availability?.accountVerification);
  const postTrackingAvailable =
    typeof availability === "boolean"
      ? availability
      : Boolean(availability?.postTracking);
  const availabilityNote =
    typeof availability === "object" && availability?.note
      ? String(availability.note)
      : null;
  const placement = verificationPlacement(selectedPlatform);
  const profileLink = verificationProfileLink(selectedPlatform);

  const totalEarned = useMemo(
    () =>
      events.reduce(
        (sum: number, event: any) => sum + Number(event.creditsGranted || 0),
        0,
      ),
    [events],
  );

  if (readOnly || (!projectId && !hideTrigger)) return null;

  const copyCode = async () => {
    const code = selectedAccount?.verificationCode;
    if (!code) return;
    const copied = await copyTextToClipboard(code);
    if (copied) {
      toast.success("Code copied.");
    } else if (selectVisibleVerificationCode(code)) {
      toast("Clipboard blocked. Code selected; press Cmd+C or Ctrl+C to copy.");
    } else {
      toast.error("Clipboard blocked. Select and copy the code manually.");
    }
  };

  return (
    <>
      {!hideTrigger && (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group fixed bottom-5 left-[56px] z-40 flex w-[252px] items-center gap-3 rounded-xl border border-white/10 bg-[#111119]/95 px-3 py-3 text-left shadow-[0_18px_48px_rgba(0,0,0,0.42)] backdrop-blur transition hover:-translate-y-0.5 hover:border-violet-300/35 hover:bg-[#15151f] max-[1199px]:bottom-[116px] max-[1199px]:left-3 max-[1199px]:w-[232px] max-[520px]:w-[calc(100vw-1.5rem)]"
        aria-label="Open rewards"
      >
        <div className="pointer-events-none absolute inset-0 rounded-xl bg-[linear-gradient(135deg,rgba(139,92,246,0.12),rgba(16,185,129,0.05)_45%,rgba(255,255,255,0.02))]" />
        <div className="relative grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-violet-300/20 bg-violet-400/10 text-violet-200 transition group-hover:border-violet-200/35 group-hover:bg-violet-400/15">
          <Zap className="h-[18px] w-[18px] fill-violet-200" />
        </div>
        <div className="relative min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-200/80">
              Rewards
            </span>
            <span className="h-1 w-1 rounded-full bg-emerald-300/70" />
          </div>
          <div className="truncate text-sm font-bold leading-5 text-white">
            Earn credits
          </div>
          <div className="truncate text-[11px] leading-4 text-white/45">
            Share this music video
          </div>
        </div>
        <div className="relative grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-white/10 bg-white/[0.04] text-white/55 transition group-hover:border-emerald-200/25 group-hover:text-emerald-100">
          <Share2 className="h-3.5 w-3.5" />
        </div>
      </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton
          className="max-h-[90vh] w-[min(1080px,calc(100vw-2rem))] overflow-y-auto border-white/10 bg-[#0f0f15] p-0 text-white sm:!max-w-[1080px]"
        >
          <div className="relative overflow-hidden border-b border-white/10 px-6 py-8">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_10%,rgba(16,185,129,0.26),transparent_34%),radial-gradient(circle_at_78%_20%,rgba(124,58,237,0.34),transparent_34%),linear-gradient(135deg,#101014,#15151f)]" />
            <div className="relative max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-white/75">
                <Share2 className="h-3.5 w-3.5" />
                Solmi Rewards
              </div>
              <DialogTitle className="text-3xl font-black leading-tight tracking-normal">
                Earn credits when your music videos get watched.
              </DialogTitle>
              <DialogDescription className="mt-3 max-w-xl text-sm leading-6 text-white/65">
                Connect a public social account, post an exported Solmi music
                video, then submit the link. Only views after submission count.
              </DialogDescription>
              <a
                href="/rewards-help"
                className="mt-4 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white/75 transition hover:bg-white/10 hover:text-white"
              >
                Rules & FAQs
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <div className="mt-5 max-w-xl rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm font-bold text-white">
                  Account Requirements
                </div>
                <p className="mt-1 text-xs leading-5 text-white/55">
                  To participate, your social media account must meet these
                  minimum requirements:
                </p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-white/70">
                  <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1">
                    At least 500 followers
                  </span>
                  <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1">
                    At least 3 posts published
                  </span>
                  <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1">
                    Account must be public
                  </span>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-white/70">
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1">
                  Earned: {formatNumber(totalEarned)} credits
                </span>
              </div>
            </div>
          </div>

          {summaryQuery.isLoading ? (
            <div className="grid min-h-[420px] place-items-center">
              <Loader2 className="h-5 w-5 animate-spin text-white/50" />
            </div>
          ) : (
            <div className="grid gap-6 p-6 lg:grid-cols-[1fr_0.9fr]">
              <div className="space-y-6">
                <section className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-bold">1. Connect account</h3>
                      <p className="text-sm text-white/55">
                        Add the code to your bio or channel description.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {PLATFORMS.map((platform) => (
                      <button
                        key={platform.id}
                        type="button"
                        onClick={() => setSelectedPlatform(platform.id)}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                          selectedPlatform === platform.id
                            ? "border-violet-300/50 bg-violet-400/15 text-white"
                            : "border-white/10 bg-white/[0.03] text-white/65 hover:text-white"
                        }`}
                      >
                        {platformIcon(platform.id)}
                        {platform.label}
                      </button>
                    ))}
                  </div>

                  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Input
                        value={handle}
                        onChange={(event) => setHandle(event.target.value)}
                        placeholder={selectedPlatformConfig.handlePlaceholder}
                        className="border-white/10 bg-black/20 text-white placeholder:text-white/35"
                      />
                      <Button
                        onClick={() =>
                          beginConnect.mutate({
                            platform: selectedPlatform,
                            handle,
                          })
                        }
                        disabled={beginConnect.isPending || !handle.trim()}
                        className="shrink-0"
                      >
                        {beginConnect.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Get code"
                        )}
                      </Button>
                    </div>

                    {!accountVerificationAvailable && (
                      <div className="mt-3 flex gap-2 rounded-lg border border-amber-300/20 bg-amber-300/10 p-3 text-xs leading-5 text-amber-100">
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                        Auto verification for {selectedPlatformConfig.label} is
                        not configured on the server yet. You can prepare the
                        account, but tracking needs the platform API key.
                      </div>
                    )}
                    {accountVerificationAvailable && !postTrackingAvailable && (
                      <div className="mt-3 flex gap-2 rounded-lg border border-amber-300/20 bg-amber-300/10 p-3 text-xs leading-5 text-amber-100">
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                        {availabilityNote ||
                          `Auto reward tracking for ${selectedPlatformConfig.label} is not available yet.`}
                      </div>
                    )}

                    {selectedAccount && (
                      <div className="mt-4 space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-bold">
                              {selectedAccount.displayName ||
                                selectedAccount.handle}
                            </div>
                            <div className="truncate text-xs text-white/50">
                              {selectedAccount.handle}
                            </div>
                          </div>
                          <StatusPill status={selectedAccount.status} />
                        </div>

                        <div className="rounded-lg border border-violet-300/20 bg-violet-300/10 p-3 text-xs leading-5 text-violet-50">
                          Add{" "}
                          <span
                            data-reward-verification-code
                            className="select-all font-mono font-black text-white"
                          >
                            {selectedAccount.verificationCode}
                          </span>{" "}
                          to your {placement}, make sure the account is public,
                          then click Verify. You can remove the code after the
                          account is verified.
                          <a
                            href={profileLink.href}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 flex w-fit items-center gap-1 font-semibold text-violet-100 underline-offset-4 hover:underline"
                          >
                            {profileLink.label}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>

                        <div className="flex flex-col gap-2 rounded-lg border border-white/10 bg-black/20 p-3 sm:flex-row sm:items-center">
                          <div className="min-w-0 flex-1">
                            <div className="text-xs text-white/45">
                              Verification code
                            </div>
                            <div
                              data-reward-verification-code
                              className="select-all font-mono text-lg font-black"
                            >
                              {selectedAccount.verificationCode}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={copyCode}
                            >
                              <Copy className="h-4 w-4" />
                              Copy
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() =>
                                verifyAccount.mutate({
                                  accountId: selectedAccount.id,
                                })
                              }
                              disabled={verifyAccount.isPending}
                            >
                              {verifyAccount.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <BadgeCheck className="h-4 w-4" />
                              )}
                              Verify
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                <section className="space-y-4">
                  <div>
                    <h3 className="text-lg font-bold">2. Submit your post</h3>
                    <p className="text-sm text-white/55">
                      Mention Solmi (or #solmiai) in the title, caption, or
                      description.
                    </p>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                    {!exportedVideoUrl && (
                      <div className="mb-3 rounded-lg border border-amber-300/20 bg-amber-300/10 p-3 text-xs leading-5 text-amber-100">
                        {projectId
                          ? "Export this music video and post on social media, then submit the social post link here."
                          : "Create and export a music video first, then submit its social post link from that project."}
                      </div>
                    )}
                    <div className="mb-3">
                      <select
                        value={activeSubmitAccountId}
                        onChange={(event) =>
                          setSelectedAccountId(event.target.value)
                        }
                        className="h-10 w-full rounded-md border border-white/10 bg-black/25 px-3 text-sm text-white outline-none"
                      >
                        {submitAccounts.length === 0 ? (
                          <option value="">No verified account yet</option>
                        ) : (
                          submitAccounts.map((account: any) => (
                            <option key={account.id} value={account.id}>
                              {account.platform} - {account.handle}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Input
                        value={postUrl}
                        onChange={(event) => setPostUrl(event.target.value)}
                        placeholder="Paste your YouTube, X, or Instagram post link"
                        className="border-white/10 bg-black/20 text-white placeholder:text-white/35"
                      />
                      <Button
                        onClick={() => {
                          if (!projectId) return;
                          submitLink.mutate({
                            projectId,
                            accountId: activeSubmitAccountId,
                            postUrl,
                          });
                        }}
                        disabled={
                          submitLink.isPending || !postUrl.trim() || !canSubmit
                        }
                        className="shrink-0"
                      >
                        {submitLink.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        Submit
                      </Button>
                    </div>
                  </div>
                </section>
              </div>

              <div className="space-y-6">
                <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <h3 className="mb-3 text-base font-bold">Reward schedule</h3>
                  <div className="space-y-2">
                    {schedule.slice(0, 5).map((milestone: any) => (
                      <div
                        key={milestone.views}
                        className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-lg bg-black/20 px-3 py-2 text-sm"
                      >
                        <span className="text-white/60">
                          {formatNumber(milestone.views)} views
                        </span>
                        <span className="text-right">
                          <span className="block font-bold text-emerald-200">
                            {formatNumber(milestone.cumulativeCredits)} credits
                          </span>
                          <span className="block text-[11px] font-semibold text-white/45">
                            {formatUsdValue(milestone.equivalentUsd)}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <h3 className="mb-3 text-base font-bold">Tracked posts</h3>
                  {submissions.length === 0 ? (
                    <div className="rounded-lg bg-black/20 p-4 text-sm text-white/50">
                      No rewards posts submitted yet.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {submissions.slice(0, 5).map((submission: any) => (
                        <div
                          key={submission.id}
                          className="rounded-lg border border-white/10 bg-black/20 p-3"
                        >
                          <div className="mb-2 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 text-sm font-bold capitalize">
                                {platformIcon(submission.platform)}
                                {submission.platform}
                              </div>
                              <a
                                href={submission.postUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-1 flex items-center gap-1 truncate text-xs text-violet-200 hover:text-violet-100"
                              >
                                Open post <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                            <StatusPill status={submission.status} />
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs text-white/55">
                            <div>
                              <div>Eligible views</div>
                              <strong className="text-white">
                                {formatNumber(submission.eligibleViews)}
                              </strong>
                            </div>
                            <div>
                              <div>Last checked</div>
                              <strong className="text-white">
                                {formatDate(submission.lastCheckedAt)}
                              </strong>
                            </div>
                          </div>
                          {submission.rejectionReason && (
                            <div className="mt-2 rounded-md bg-amber-300/10 px-2 py-1.5 text-xs text-amber-100">
                              {submission.rejectionReason}
                            </div>
                          )}
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="mt-3 w-full"
                            onClick={() =>
                              refreshSubmission.mutate({
                                submissionId: submission.id,
                              })
                            }
                            disabled={refreshSubmission.isPending}
                          >
                            {refreshSubmission.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                            Refresh
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
